import { useMemo } from 'react'

import { useQueries } from '@tanstack/react-query'
import { CellValueChangedEvent } from 'ag-grid-enterprise'

import { AgGridWidget } from '@fortem-monorepo/react-ag'
import { useApi, useUserContext } from '@fortem-monorepo/react-spa'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import { Website } from '@fortem-monorepo/web-sdk'

import { Page } from '@/components/Page'
import { FLOW } from '@/constants/links'
import {
  createAllProductsColDefs,
  createProductDetailsColDefs,
} from '@/pages/flow/website/col-defs'
import {
  useUpdateAllProductsMutation,
  useUpdateProductDetailsMutation,
} from '@/react-query/mutations/website'

export interface FlowWebsitePageProps {}

export const FlowWebsitePage: React.FC<FlowWebsitePageProps> = ({}) => {
  const { hasPermissions } = useUserContext()

  const hasWriteAccess = hasPermissions((permissions) => ({
    permissions: [permissions.WRITE_WEBSITE],
  }))

  const api = useApi()

  const queries = useQueries({
    queries: [
      {
        queryKey: ['flow', 'website', 'product-details'],
        queryFn: () => api.website.getInvestmentSecurity(),
      },
      {
        queryKey: ['flow', 'website', 'all-products'],
        queryFn: () => api.website.getSecondaryMarket(),
      },
    ],
  })

  const [productDetails, productsPage] = queries

  const [productDetailsData, productsPageData] = useMemo(() => {
    return [productDetails.data ?? [], productsPage.data ?? []]
  }, [productDetails.data, productsPage.data])

  const updateProductDetailsMutation = useUpdateProductDetailsMutation()
  const updateAllProductsMutation = useUpdateAllProductsMutation()

  return (
    <Page metadata={{ title: 'Website' }} permissions={FLOW.WEBSITE.permissions}>
      <ResizablePanelGroup direction={'horizontal'}>
        <ResizablePanel>
          <AgGridWidget
            query={productDetails}
            widget={{ exposed: true, className: 'h-full' }}
            widgetHeader={{ title: 'Website - Product Details - Prod' }}
            grid={{
              rowData: productDetailsData,
              columnDefs: createProductDetailsColDefs(hasWriteAccess),
              domLayout: 'normal',
              onCellValueChanged: (
                params: CellValueChangedEvent<Website.GetInvestmentSecurityData[number]>,
              ) => {
                return updateProductDetailsMutation.mutateAsync({
                  rowData: params.data,
                  oldValue: params.oldValue,
                  newValue: params.newValue,
                  updatedField: params.colDef.field!,
                })
              },
            }}
          />
        </ResizablePanel>
        <ResizableHandle />
        <ResizablePanel>
          <AgGridWidget
            query={productsPage}
            widget={{ exposed: true, className: 'h-full' }}
            widgetHeader={{ title: 'Website - All Products - Prod' }}
            grid={{
              rowData: productsPageData,
              columnDefs: createAllProductsColDefs(hasWriteAccess),
              domLayout: 'normal',
              onCellValueChanged: (
                params: CellValueChangedEvent<Website.GetSecondaryMarketData[number]>,
              ) => {
                return updateAllProductsMutation.mutateAsync({
                  rowData: params.data,
                  oldValue: params.oldValue,
                  newValue: params.newValue,
                  updatedField: params.colDef.field!,
                })
              },
            }}
          />
        </ResizablePanel>
      </ResizablePanelGroup>
    </Page>
  )
}
